import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Image, Save, Edit, Check, X } from 'lucide-react';
import { OCRResult, ExtractedQuestion } from '@/services/ocrService';

interface ExtractedQuestionsPreviewProps {
  result: OCRResult;
  onSaveQuestions: (questions: ExtractedQuestion[]) => void;
  onClose: () => void;
}

const ExtractedQuestionsPreview: React.FC<ExtractedQuestionsPreviewProps> = ({
  result,
  onSaveQuestions,
  onClose
}) => {
  const [questions, setQuestions] = useState<ExtractedQuestion[]>(result.questions);
  const [selectedQuestions, setSelectedQuestions] = useState<Set<number>>(
    new Set(result.questions.map((_, index) => index))
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedQuestion, setEditedQuestion] = useState<ExtractedQuestion | null>(null);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedQuestions(new Set(questions.map((_, index) => index)));
    } else {
      setSelectedQuestions(new Set());
    }
  };

  const handleSelectQuestion = (index: number, checked: boolean) => {
    const newSelected = new Set(selectedQuestions);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedQuestions(newSelected);
  };

  const handleEditQuestion = (index: number) => {
    setEditingIndex(index);
    setEditedQuestion({ ...questions[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editedQuestion) {
      const newQuestions = [...questions];
      newQuestions[editingIndex] = editedQuestion;
      setQuestions(newQuestions);
      setEditingIndex(null);
      setEditedQuestion(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedQuestion(null);
  };

  const handleSaveSelected = () => {
    const selectedQuestionsArray = questions.filter((_, index) =>
    selectedQuestions.has(index)
    );
    onSaveQuestions(selectedQuestionsArray);
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':return 'bg-blue-100 text-blue-800';
      case 'true_false':return 'bg-green-100 text-green-800';
      case 'short_answer':return 'bg-yellow-100 text-yellow-800';
      case 'essay':return 'bg-purple-100 text-purple-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':return 'bg-green-100 text-green-800';
      case 'medium':return 'bg-yellow-100 text-yellow-800';
      case 'hard':return 'bg-red-100 text-red-800';
      default:return 'bg-gray-100 text-gray-800';
    }
  };

  const formatQuestionType = (type: string) => {
    return type.split('_').map((word) =>
    word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="space-y-6" data-id="lbf4owl4a" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
      {/* Source Information */}
      <Card data-id="3lf7fcznx" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
        <CardHeader data-id="0xkp6mtjx" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
          <CardTitle className="flex items-center gap-2" data-id="1inmico1n" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
            {result.sourceType === 'pdf' ?
            <FileText className="h-5 w-5 text-red-500" data-id="habpyot38" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" /> :

            <Image className="h-5 w-5 text-blue-500" data-id="stipipayl" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
            }
            Extracted from: {result.fileName}
          </CardTitle>
          <CardDescription data-id="vsxw9c5dy" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
            Found {result.questions.length} questions from {result.sourceType.toUpperCase()} file
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Selection Controls */}
      <Card data-id="ot9efg513" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
        <CardContent className="pt-6" data-id="4j4d4mapm" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
          <div className="flex items-center justify-between" data-id="81t9k5zvc" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
            <div className="flex items-center space-x-2" data-id="6lsr8oc7g" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
              <Checkbox
                id="select-all"
                checked={selectedQuestions.size === questions.length}
                onCheckedChange={handleSelectAll} data-id="x206279h1" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />

              <Label htmlFor="select-all" data-id="ajxt3gxh9" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                Select All ({selectedQuestions.size} of {questions.length} selected)
              </Label>
            </div>
            <div className="flex gap-2" data-id="rg5obqpik" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
              <Button variant="outline" onClick={onClose} data-id="0bf0lhmgj" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                Cancel
              </Button>
              <Button
                onClick={handleSaveSelected}
                disabled={selectedQuestions.size === 0} data-id="tkth39m25" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">

                <Save className="h-4 w-4 mr-2" data-id="9tieurql9" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                Save Selected Questions
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Questions List */}
      <div className="space-y-4" data-id="hexp70nnv" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
        {questions.map((question, index) =>
        <Card key={index} className={selectedQuestions.has(index) ? 'ring-2 ring-blue-500' : ''} data-id="ppijyiwu8" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
            <CardContent className="pt-6" data-id="3nmmz1se5" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
              <div className="flex items-start justify-between mb-4" data-id="g2xcluol0" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                <div className="flex items-center space-x-2" data-id="2dzfdo0pc" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                  <Checkbox
                  checked={selectedQuestions.has(index)}
                  onCheckedChange={(checked) => handleSelectQuestion(index, checked as boolean)} data-id="99vi28nuk" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />

                  <span className="font-medium" data-id="rzdy1mwcy" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Question {index + 1}</span>
                </div>
                <div className="flex items-center gap-2" data-id="smzb2nkyx" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                  <Badge className={getQuestionTypeColor(question.question_type)} data-id="i3cp0ey4q" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    {formatQuestionType(question.question_type)}
                  </Badge>
                  <Badge className={getDifficultyColor(question.difficulty)} data-id="bt10w8tst" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    {question.difficulty}
                  </Badge>
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditQuestion(index)}
                  disabled={editingIndex === index} data-id="9l1ohdren" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">

                    <Edit className="h-4 w-4" data-id="5xa4t37sc" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                  </Button>
                </div>
              </div>

              {editingIndex === index && editedQuestion ?
            // Edit Mode
            <div className="space-y-4" data-id="kfmyav3hc" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                  <div className="space-y-2" data-id="pyqixmwk7" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <Label data-id="n8dn9opp8" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Question Text</Label>
                    <Textarea
                  value={editedQuestion.question_text}
                  onChange={(e) => setEditedQuestion({
                    ...editedQuestion,
                    question_text: e.target.value
                  })}
                  rows={3} data-id="k3qcex5n4" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />

                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="nvjdb9cdz" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <div className="space-y-2" data-id="p0nef8xqy" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <Label data-id="u42u6u02c" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Question Type</Label>
                      <Select
                    value={editedQuestion.question_type}
                    onValueChange={(value: any) => setEditedQuestion({
                      ...editedQuestion,
                      question_type: value
                    })} data-id="yofrsh55u" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">

                        <SelectTrigger data-id="207ml9qtb" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                          <SelectValue data-id="qmzpn1udw" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                        </SelectTrigger>
                        <SelectContent data-id="143by1ka1" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                          <SelectItem value="multiple_choice" data-id="992q28zsf" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Multiple Choice</SelectItem>
                          <SelectItem value="true_false" data-id="5qud1a328" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">True/False</SelectItem>
                          <SelectItem value="short_answer" data-id="nflkvio9w" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Short Answer</SelectItem>
                          <SelectItem value="essay" data-id="6pwl4jauj" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Essay</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2" data-id="d4sov6vrf" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <Label data-id="3sdsfppxw" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Difficulty</Label>
                      <Select
                    value={editedQuestion.difficulty}
                    onValueChange={(value: any) => setEditedQuestion({
                      ...editedQuestion,
                      difficulty: value
                    })} data-id="earg2i598" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">

                        <SelectTrigger data-id="7pqju174h" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                          <SelectValue data-id="mhcqm5lvm" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                        </SelectTrigger>
                        <SelectContent data-id="fqjghex81" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                          <SelectItem value="easy" data-id="8xcfa4i1d" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Easy</SelectItem>
                          <SelectItem value="medium" data-id="tght98vys" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Medium</SelectItem>
                          <SelectItem value="hard" data-id="phwjzxlye" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Hard</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="rd8z3hvos" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <Label data-id="xaan3r17w" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Answer</Label>
                    <Textarea
                  value={editedQuestion.answer_text}
                  onChange={(e) => setEditedQuestion({
                    ...editedQuestion,
                    answer_text: e.target.value
                  })}
                  rows={2} data-id="5hahzrvc9" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />

                  </div>

                  {editedQuestion.question_type === 'multiple_choice' &&
              <div className="space-y-2" data-id="ir3z38exi" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <Label data-id="ng0awxwi9" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Options (one per line)</Label>
                      <Textarea
                  value={editedQuestion.options?.join('\n') || ''}
                  onChange={(e) => setEditedQuestion({
                    ...editedQuestion,
                    options: e.target.value.split('\n').filter((opt) => opt.trim())
                  })}
                  rows={4}
                  placeholder="Option 1&#10;Option 2&#10;Option 3&#10;Option 4" data-id="u4ydau3qj" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />

                    </div>
              }

                  <div className="flex gap-2" data-id="a9vyiqs1s" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <Button size="sm" onClick={handleSaveEdit} data-id="ourdqfr98" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <Check className="h-4 w-4 mr-2" data-id="0evv5k512" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit} data-id="tt2alqvwk" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <X className="h-4 w-4 mr-2" data-id="np5qdr3ds" data-path="src/components/questions/ExtractedQuestionsPreview.tsx" />
                      Cancel
                    </Button>
                  </div>
                </div> :

            // View Mode
            <div className="space-y-3" data-id="zg29rhi7n" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                  <div data-id="xioksdogm" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <Label className="text-sm font-medium" data-id="audh3fyc3" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Question:</Label>
                    <p className="mt-1 text-gray-900" data-id="zpcewd28i" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">{question.question_text}</p>
                  </div>

                  <div data-id="3dadmcv9p" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <Label className="text-sm font-medium" data-id="2f6ftt60t" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Answer:</Label>
                    <p className="mt-1 text-gray-700" data-id="ip561z0ma" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">{question.answer_text}</p>
                  </div>

                  {question.question_type === 'multiple_choice' && question.options &&
              <div data-id="my4aekgk0" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                      <Label className="text-sm font-medium" data-id="cyvcj9fsa" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Options:</Label>
                      <ul className="mt-1 list-disc list-inside text-gray-700" data-id="fqs8zy2m7" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                        {question.options.map((option, optIndex) =>
                  <li key={optIndex} data-id="on3rmms06" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">{option}</li>
                  )}
                      </ul>
                    </div>
              }

                  <div className="flex gap-4 text-sm text-gray-600" data-id="py812obye" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
                    <span data-id="7yxtyckpp" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Subject: {question.subject}</span>
                    <span data-id="i2cc5ter4" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Type: {formatQuestionType(question.question_type)}</span>
                    <span data-id="qukbgjhjt" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">Difficulty: {question.difficulty}</span>
                  </div>
                </div>
            }
            </CardContent>
          </Card>
        )}
      </div>

      {questions.length === 0 &&
      <Card data-id="ixnvr0v2s" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
          <CardContent className="pt-6 text-center text-gray-500" data-id="m4dkeylzr" data-path="src/components/questions/ExtractedQuestionsPreview.tsx">
            No questions were extracted from the file. Please try with a different file or check the content quality.
          </CardContent>
        </Card>
      }
    </div>);

};

export default ExtractedQuestionsPreview;